/**
 * FmGallerySpecimenPicker — the one specimen picker of the visual grammar's
 * archetype pages. Every archetype page switches through the same eight page
 * states; specimens that show archetype-specific behaviour (map modes, form
 * stages, operational mutations) are listed apart as variants, so no state is
 * shown twice. Production routes never render it.
 */
import React from 'react';
import { FmButton } from './FmButton';
import { FmInline, FmStack, FmText } from './FmComposition';
import { FmSelector } from './FmSelector';
import {
  assertGalleryStatePage,
  type FmGallerySpecimen,
  FM_GALLERY_PAGE_STATES,
  type FmGalleryPageState,
  type FmGalleryStatePage,
} from './galleryPageStates';

export type { FmGalleryPageState, FmGalleryStatePage } from './galleryPageStates';

const STATE_LABELS: Readonly<Record<FmGalleryPageState, string>> = {
  ready: 'Ready',
  loading: 'Loading',
  empty: 'Empty',
  partial: 'Partial',
  stale: 'Stale',
  locked: 'Locked',
  error: 'Error',
  'long-copy': 'Long copy',
};

export interface FmGallerySpecimenPickerProps<Specimen extends FmGallerySpecimen> {
  /** Archetype name, used for the accessible names of both pickers. */
  archetype: string;
  specimens: readonly Specimen[];
  states: FmGalleryStatePage;
  activeId: string;
  onSelect: (specimen: Specimen) => void;
  /** Each control's test id is this prefix plus the specimen id it opens. */
  testIdPrefix: string;
}

export function FmGallerySpecimenPicker<Specimen extends FmGallerySpecimen>({
  archetype,
  specimens,
  states,
  activeId,
  onSelect,
  testIdPrefix,
}: FmGallerySpecimenPickerProps<Specimen>): React.ReactElement {
  assertGalleryStatePage(states, specimens, archetype);
  const byId = new Map(specimens.map((specimen) => [specimen.id, specimen]));
  const stateIds = new Set(Object.values(states));
  const activeState = FM_GALLERY_PAGE_STATES.find((state) => states[state] === activeId) ?? null;
  const variants = specimens.filter(({ id }) => !stateIds.has(id));
  const select = (id: string): void => {
    const specimen = byId.get(id);
    if (!specimen) throw new Error(`${archetype} has no specimen "${id}"`);
    onSelect(specimen);
  };
  return (
    <FmStack gap="sm" data-testid={`${testIdPrefix}picker`}>
      <FmText size="fine" tone="muted" eyebrow>
        Page state
      </FmText>
      <FmSelector
        variant="chips"
        label={`${archetype} page state`}
        value={activeState}
        onChange={(state) => select(states[state as FmGalleryPageState])}
        items={FM_GALLERY_PAGE_STATES.map((state) => ({
          value: state,
          label: STATE_LABELS[state],
          'data-testid': `${testIdPrefix}${states[state]}`,
        }))}
        data-testid={`${testIdPrefix}states`}
      />
      {variants.length > 0 ? (
        <>
          <FmText size="fine" tone="muted" eyebrow>
            Archetype behaviour
          </FmText>
          <FmInline gap="xs" wrap data-testid={`${testIdPrefix}variants`}>
            {variants.map((specimen) => (
              <FmButton
                key={specimen.id}
                variant={specimen.id === activeId ? 'primary' : 'bordered'}
                aria-pressed={specimen.id === activeId}
                onClick={() => onSelect(specimen)}
                data-testid={`${testIdPrefix}${specimen.id}`}
              >
                {specimen.label}
              </FmButton>
            ))}
          </FmInline>
        </>
      ) : null}
    </FmStack>
  );
}
